import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface PayeeAlert {
  id: string;
  type: 'increase' | 'recurring' | 'abnormal';
  payeeName: string;
  payeeId: string | null;
  message: string;
  currentAmount: number;
  previousAmount: number | null;
  changePercent: number | null;
  priority: 'high' | 'medium' | 'low';
}

// ─── Formatting helper ────────────────────────────────────────────────────
const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const usePayeeAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<PayeeAlert[]>([]);
  const [loading, setLoading] = useState(true);

  const analyze = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);

    const now = new Date();
    const currentStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const currentEnd   = now.toISOString().split('T')[0];

    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevStart = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}-01`;
    const prevEnd   = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];

    // Fetch current + previous month expenses with payee info
    const [currRes, prevRes] = await Promise.all([
      supabase.from('transactions')
        .select('payee_id, payee_name, amount')
        .eq('user_id', user.id)
        .eq('type', 'despesa')
        .is('deleted_at', null)
        .neq('transaction_subtype', 'transferencia')
        .gte('date', currentStart)
        .lte('date', currentEnd),
      supabase.from('transactions')
        .select('payee_id, payee_name, amount, date')
        .eq('user_id', user.id)
        .eq('type', 'despesa')
        .is('deleted_at', null)
        .neq('transaction_subtype', 'transferencia')
        .gte('date', prevStart)
        .lte('date', prevEnd),
    ]);

    const currTxs = (currRes.data ?? []) as { payee_id: string | null; payee_name: string | null; amount: number }[];
    const prevTxs = (prevRes.data ?? []) as { payee_id: string | null; payee_name: string | null; amount: number; date: string }[];

    // ── Group by payee ────────────────────────────────────────────────────
    const buildMap = (txs: typeof currTxs) => {
      const map: Record<string, { id: string | null; total: number; count: number }> = {};
      txs.forEach(t => {
        const key = t.payee_name ?? '';
        if (!key) return;
        if (!map[key]) map[key] = { id: t.payee_id, total: 0, count: 0 };
        map[key].total += Number(t.amount);
        map[key].count += 1;
      });
      return map;
    };

    const curr = buildMap(currTxs);
    const prev = buildMap(prevTxs);

    const newAlerts: PayeeAlert[] = [];

    // ── Alert 1: Variation vs previous month ─────────────────────────────
    Object.entries(curr).forEach(([name, data]) => {
      const prevData = prev[name];
      if (!prevData || prevData.total === 0) return;
      const change = ((data.total - prevData.total) / prevData.total) * 100;
      if (change >= 20) {
        newAlerts.push({
          id: `increase-${name}`,
          type: 'increase',
          payeeName: name,
          payeeId: data.id,
          message: `Gasto com ${name} aumentou ${Math.round(change)}% este mês (${fmt(data.total)} vs ${fmt(prevData.total)})`,
          currentAmount: data.total,
          previousAmount: prevData.total,
          changePercent: Math.round(change),
          priority: change >= 50 ? 'high' : 'medium',
        });
      }
    });

    // ── Alert 2: Recurring pattern detected ──────────────────────────────
    // Payee appears in both current and previous month (recurring behavior)
    Object.entries(curr).forEach(([name, data]) => {
      const prevData = prev[name];
      if (!prevData) return;
      const change = prevData.total > 0 ? ((data.total - prevData.total) / prevData.total) * 100 : 0;
      if (Math.abs(change) < 5 && data.count >= 1 && prevData.count >= 1) {
        // Stable recurring — low priority positive insight
        if (!newAlerts.find(a => a.payeeName === name)) {
          newAlerts.push({
            id: `recurring-${name}`,
            type: 'recurring',
            payeeName: name,
            payeeId: data.id,
            message: `Pagamento recorrente detectado: ${name} (${fmt(data.total)}/mês)`,
            currentAmount: data.total,
            previousAmount: prevData.total,
            changePercent: Math.round(change),
            priority: 'low',
          });
        }
      }
    });

    // ── Alert 3: Abnormal spend — new payee with high amount ─────────────
    const avgExpense = currTxs.length > 0
      ? currTxs.reduce((s, t) => s + Number(t.amount), 0) / currTxs.length
      : 0;

    Object.entries(curr).forEach(([name, data]) => {
      const prevData = prev[name];
      if (prevData) return; // Not new
      if (data.total > avgExpense * 3 && data.total > 200) {
        newAlerts.push({
          id: `abnormal-${name}`,
          type: 'abnormal',
          payeeName: name,
          payeeId: data.id,
          message: `Gasto incomum com ${name}: ${fmt(data.total)} (novo favorecido este mês)`,
          currentAmount: data.total,
          previousAmount: null,
          changePercent: null,
          priority: 'high',
        });
      }
    });

    // Sort by priority
    const order = { high: 0, medium: 1, low: 2 };
    newAlerts.sort((a, b) => order[a.priority] - order[b.priority]);

    setAlerts(newAlerts.slice(0, 10));
    setLoading(false);
  }, [user]);

  useEffect(() => { analyze(); }, [analyze]);

  return { alerts, loading, refetch: analyze };
};
